import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiClient } from "@/lib/api-client";
import { MapPin, Loader2, X } from "lucide-react";
import type { Property } from "@/api/generated/client/models";

interface SearchResult { id: number; label: string }

function formatPropertyLabel(p: Property): string {
    const parts = [p.streetAddress, p.city, p.state, p.zipCode].filter(Boolean);
    return parts.join(", ");
}

/**
 * CreatePropertiesPicker — for create dialogs. Manages pending property IDs in memory.
 */
export function CreatePropertiesPicker({ selectedIds, onChange }: { selectedIds: number[]; onChange: (ids: number[]) => void }) {
    const [search, setSearch] = useState("");
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const labelCache = useRef<Map<number, string>>(new Map());

    useEffect(() => {
        if (!search) { setResults([]); setIsSearching(false); return; }
        setIsSearching(true);
        const timer = setTimeout(async () => {
            try {
                const res = await apiClient.properties.listProperties({ search, limit: 15 });
                const properties = (res.properties || [])
                    .filter((p: Property) => !selectedIds.includes(p.id))
                    .map((p: Property) => ({ id: p.id, label: formatPropertyLabel(p) || `Property #${p.id}` }));
                setResults(properties);
            } catch { setResults([]); }
            setIsSearching(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [search, selectedIds]);

    const selectedLabels = selectedIds.map(id => ({ id, label: labelCache.current.get(id) || `Property #${id}` }));

    return (
        <div className="space-y-3">
            <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Link Properties</Label>
            {selectedLabels.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedLabels.map(({ id, label }) => (
                        <Badge key={id} variant="secondary" className="gap-1 pr-1">
                            {label}
                            <button type="button" aria-label={`Remove ${label}`} onClick={() => onChange(selectedIds.filter(sid => sid !== id))} className="ml-0.5 rounded-full hover:bg-muted p-0.5">
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}
            <div>
                <div className="relative">
                    <Input placeholder="Search properties..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-sm pr-8" />
                    {isSearching && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground animate-spin" />}
                </div>
                {!isSearching && search && results.length > 0 && (
                    <div className="border rounded-md max-h-32 overflow-y-auto overflow-x-hidden mt-1">
                        {results.slice(0, 8).map((p) => (
                            <button key={p.id} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2 min-w-0" onClick={() => { labelCache.current.set(p.id, p.label); onChange([...selectedIds, p.id]); setSearch(""); }}>
                                <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span className="truncate">{p.label}</span>
                            </button>
                        ))}
                    </div>
                )}
                {!isSearching && search && results.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">No matching properties</p>
                )}
            </div>
        </div>
    );
}
